import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";
import { ProNavigation } from "@/components/ProNavigation";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { 
  Plus, 
  Mail, 
  Calendar as CalendarIcon, 
  Send, 
  Clock, 
  Edit, 
  Trash2, 
  Eye,
  Copy,
  History,
  CheckCircle,
  AlertCircle,
  Loader2,
  FileText,
  Sparkles,
  Image as ImageIcon,
  BookOpen,
  Lightbulb,
  PenTool,
  Target,
  Heart,
  Leaf,
  AlertTriangle,
  Users
} from "lucide-react";
import BrevoContactsList from "@/components/newsletter/BrevoContactsList";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

interface Newsletter {
  id: string;
  title_internal: string;
  user_id: string;
  subject: string;
  preheader: string | null;
  content_html: string;
  content_json: any;
  cover_image_url: string | null;
  cta_label: string | null;
  cta_url: string | null;
  status: string;
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
}

interface SendLog {
  id: string;
  newsletter_id: string;
  run_type: string;
  brevo_campaign_id: string | null;
  recipients_count: number | null;
  success: boolean;
  error_message: string | null;
  created_at: string;
}

const NewsletterManagement = () => {
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [sendLogs, setSendLogs] = useState<SendLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [selectedNewsletter, setSelectedNewsletter] = useState<Newsletter | null>(null);
  const [sending, setSending] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [generatingIdeas, setGeneratingIdeas] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title_internal: "",
    subject: "",
    preheader: "",
    content_html: "",
    cover_image_url: "",
    cta_label: "",
    cta_url: ""
  });

  // Schedule state
  const [scheduleDate, setScheduleDate] = useState<Date | undefined>(undefined);
  const [scheduleTime, setScheduleTime] = useState("09:00");

  useEffect(() => {
    loadNewsletters();
  }, []);

  const loadNewsletters = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("newsletters")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setNewsletters(data || []);
    } catch (error: any) {
      console.error("Erreur chargement newsletters:", error);
      toast.error("Erreur lors du chargement des newsletters");
    } finally {
      setLoading(false);
    }
  };

  const loadSendLogs = async (newsletterId: string) => {
    try {
      const { data, error } = await supabase
        .from("newsletter_send_logs")
        .select("*")
        .eq("newsletter_id", newsletterId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setSendLogs(data || []);
    } catch (error: any) {
      console.error("Erreur chargement logs:", error);
    }
  };

  const handleCreate = () => {
    setSelectedNewsletter(null);
    setFormData({
      title_internal: "",
      subject: "",
      preheader: "",
      content_html: "",
      cover_image_url: "",
      cta_label: "",
      cta_url: ""
    });
    setShowEditor(true);
  };

  const handleEdit = (newsletter: Newsletter) => {
    setSelectedNewsletter(newsletter);
    setFormData({
      title_internal: newsletter.title_internal,
      subject: newsletter.subject,
      preheader: newsletter.preheader || "",
      content_html: newsletter.content_html,
      cover_image_url: newsletter.cover_image_url || "",
      cta_label: newsletter.cta_label || "",
      cta_url: newsletter.cta_url || ""
    });
    setShowEditor(true);
  };

  const handleSave = async (asDraft = true) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté");
        return;
      }

      if (!formData.title_internal || !formData.subject || !formData.content_html) {
        toast.error("Veuillez remplir les champs obligatoires");
        return;
      }

      const newsletterData = {
        ...formData,
        user_id: user.id,
        status: asDraft ? "draft" : "scheduled"
      };

      if (selectedNewsletter) {
        const { error } = await supabase
          .from("newsletters")
          .update(newsletterData)
          .eq("id", selectedNewsletter.id);

        if (error) throw error;
        toast.success("Newsletter mise à jour");
      } else {
        const { error } = await supabase
          .from("newsletters")
          .insert([newsletterData]);

        if (error) throw error;
        toast.success("Newsletter créée");
      }

      setShowEditor(false);
      loadNewsletters();
    } catch (error: any) {
      console.error("Erreur sauvegarde:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleSchedule = async () => {
    if (!selectedNewsletter || !scheduleDate) {
      toast.error("Veuillez sélectionner une date");
      return;
    }

    try {
      setScheduling(true);
      
      // Combine date and time in Europe/Paris timezone
      const [hours, minutes] = scheduleTime.split(":").map(Number);
      const scheduledAt = new Date(scheduleDate);
      scheduledAt.setHours(hours, minutes, 0, 0);

      const { error } = await supabase
        .from("newsletters")
        .update({ 
          status: "scheduled", 
          scheduled_at: scheduledAt.toISOString() 
        })
        .eq("id", selectedNewsletter.id);

      if (error) throw error;

      toast.success(`Newsletter programmée pour le ${format(scheduledAt, "d MMMM yyyy à HH:mm", { locale: fr })}`);
      setShowScheduler(false);
      loadNewsletters();
    } catch (error: any) {
      console.error("Erreur programmation:", error);
      toast.error("Erreur lors de la programmation");
    } finally {
      setScheduling(false);
    }
  };

  const handleSendNow = async (newsletter: Newsletter) => {
    if (newsletter.status === "sent") {
      toast.error("Cette newsletter a déjà été envoyée");
      return;
    }

    try {
      setSending(true);
      
      const { data, error } = await supabase.functions.invoke("newsletter-send", {
        body: { 
          newsletter_id: newsletter.id, 
          run_type: "manual" 
        }
      });

      if (error) throw error;

      if (data?.success) {
        toast.success(`Newsletter envoyée à ${data.recipients_count || 0} abonné(s)`);
        loadNewsletters();
      } else {
        throw new Error(data?.error || "Erreur lors de l'envoi");
      }
    } catch (error: any) {
      console.error("Erreur envoi:", error);
      toast.error(error.message || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const handleDuplicate = async (newsletter: Newsletter) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from("newsletters")
        .insert([{
          title_internal: `${newsletter.title_internal} (copie)`,
          subject: newsletter.subject,
          preheader: newsletter.preheader,
          content_html: newsletter.content_html,
          content_json: newsletter.content_json,
          cover_image_url: newsletter.cover_image_url,
          cta_label: newsletter.cta_label,
          cta_url: newsletter.cta_url,
          status: "draft",
          user_id: user.id
        }]);

      if (error) throw error;
      toast.success("Newsletter dupliquée");
      loadNewsletters();
    } catch (error: any) {
      console.error("Erreur duplication:", error);
      toast.error("Erreur lors de la duplication");
    }
  };

  const handleDelete = async (newsletter: Newsletter) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette newsletter ?")) return;

    try {
      const { error } = await supabase
        .from("newsletters")
        .delete()
        .eq("id", newsletter.id);

      if (error) throw error;
      toast.success("Newsletter supprimée");
      loadNewsletters();
    } catch (error: any) {
      console.error("Erreur suppression:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleViewLogs = async (newsletter: Newsletter) => {
    setSelectedNewsletter(newsletter);
    await loadSendLogs(newsletter.id);
    setShowLogs(true);
  };

  const handlePreview = (newsletter: Newsletter) => {
    setSelectedNewsletter(newsletter);
    setShowPreview(true);
  };

  const openScheduler = (newsletter: Newsletter) => {
    setSelectedNewsletter(newsletter);
    if (newsletter.scheduled_at) {
      const date = parseISO(newsletter.scheduled_at);
      setScheduleDate(date);
      setScheduleTime(format(date, "HH:mm"));
    } else {
      setScheduleDate(undefined);
      setScheduleTime("09:00");
    }
    setShowScheduler(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "draft":
        return <Badge variant="secondary"><FileText className="w-3 h-3 mr-1" />Brouillon</Badge>;
      case "scheduled":
        return <Badge variant="outline" className="border-blue-500 text-blue-600"><Clock className="w-3 h-3 mr-1" />Programmée</Badge>;
      case "sent":
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Envoyée</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Generate email preview HTML with header and footer
  const generatePreviewHtml = (newsletter: Newsletter) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { font-family: 'Georgia', serif; margin: 0; padding: 20px; background: #f5f5f5; }
          .container { max-width: 600px; margin: 0 auto; background: white; border-radius: 8px; overflow: hidden; }
          .header { background: linear-gradient(135deg, #2d5016 0%, #4a7c23 100%); color: white; padding: 30px; text-align: center; }
          .header h1 { margin: 0; font-size: 24px; font-weight: normal; letter-spacing: 2px; }
          .content { padding: 30px; line-height: 1.8; color: #333; }
          ${newsletter.cover_image_url ? `.cover-image { width: 100%; height: auto; display: block; }` : ""}
          .cta { text-align: center; margin: 30px 0; }
          .cta a { display: inline-block; background: #4a7c23; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px; }
          .footer { background: #f5f5f5; padding: 20px; text-align: center; font-size: 12px; color: #666; }
          .footer a { color: #4a7c23; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>REGARD DE NATURE</h1>
          </div>
          ${newsletter.cover_image_url ? `<img src="${newsletter.cover_image_url}" alt="Image de couverture" class="cover-image" />` : ""}
          <div class="content">
            ${newsletter.content_html}
            ${newsletter.cta_label && newsletter.cta_url ? `
              <div class="cta">
                <a href="${newsletter.cta_url}">${newsletter.cta_label}</a>
              </div>
            ` : ""}
          </div>
          <div class="footer">
            <p>Vous recevez cet email car vous êtes inscrit(e) à la newsletter de Regard de Nature.</p>
            <p><a href="{{unsubscribe}}">Se désinscrire</a> | <a href="https://regard-de-nature.fr">Visiter le site</a></p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <ProNavigation />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-serif font-bold text-primary flex items-center gap-3">
              <Mail className="w-8 h-8" />
              Gestion des Newsletters
            </h1>
            <p className="text-muted-foreground mt-1">
              Créez, planifiez et envoyez vos newsletters à vos abonnés
            </p>
          </div>
          <Button onClick={handleCreate} className="gap-2">
            <Plus className="w-4 h-4" />
            Créer une newsletter
          </Button>
        </div>

        <Tabs defaultValue="newsletters" className="space-y-6">
          <div className="overflow-x-auto -mx-4 px-4 pb-2 sm:mx-0 sm:px-0">
            <TabsList className="inline-flex w-max sm:grid sm:w-full sm:grid-cols-3">
              <TabsTrigger value="newsletters" className="gap-2 flex-shrink-0 whitespace-nowrap">
                <Mail className="w-4 h-4" />
                <span className="hidden sm:inline">Newsletters</span>
                <span className="sm:hidden">News</span>
              </TabsTrigger>
              <TabsTrigger value="contacts" className="gap-2 flex-shrink-0 whitespace-nowrap">
                <Users className="w-4 h-4" />
                Contacts
              </TabsTrigger>
              <TabsTrigger value="history" className="gap-2 flex-shrink-0 whitespace-nowrap">
                <History className="w-4 h-4" />
                Historique
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="newsletters">
            <Card>
              <CardHeader>
                <CardTitle>Mes newsletters</CardTitle>
                <CardDescription>
                  Gérez vos newsletters : créez, modifiez, planifiez ou envoyez immédiatement
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : newsletters.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Mail className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Aucune newsletter créée</p>
                    <p className="text-sm">Commencez par créer votre première newsletter</p>
                    <Button onClick={handleCreate} className="mt-4 gap-2">
                      <Plus className="w-4 h-4" />
                      Créer une newsletter
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Titre interne</TableHead>
                        <TableHead>Objet</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Date planifiée</TableHead>
                        <TableHead>Date d'envoi</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {newsletters.map((newsletter) => (
                        <TableRow key={newsletter.id}>
                          <TableCell className="font-medium">{newsletter.title_internal}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{newsletter.subject}</TableCell>
                          <TableCell>{getStatusBadge(newsletter.status)}</TableCell>
                          <TableCell>
                            {newsletter.scheduled_at 
                              ? format(parseISO(newsletter.scheduled_at), "d MMM yyyy HH:mm", { locale: fr })
                              : "-"
                            }
                          </TableCell>
                          <TableCell>
                            {newsletter.sent_at 
                              ? format(parseISO(newsletter.sent_at), "d MMM yyyy HH:mm", { locale: fr })
                              : "-"
                            }
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => handlePreview(newsletter)}
                                title="Prévisualiser"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {newsletter.status !== "sent" && (
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  onClick={() => handleEdit(newsletter)}
                                  title="Modifier"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              )}
                              {newsletter.status !== "sent" && (
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  onClick={() => openScheduler(newsletter)}
                                  title="Planifier"
                                >
                                  <CalendarIcon className="w-4 h-4" />
                                </Button>
                              )}
                              {newsletter.status !== "sent" && (
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  onClick={() => handleSendNow(newsletter)}
                                  disabled={sending}
                                  title="Envoyer maintenant"
                                >
                                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => handleDuplicate(newsletter)}
                                title="Dupliquer"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => handleViewLogs(newsletter)}
                                title="Voir les logs"
                              >
                                <History className="w-4 h-4" />
                              </Button>
                              {newsletter.status === "draft" && (
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  onClick={() => handleDelete(newsletter)}
                                  title="Supprimer"
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contacts">
            <BrevoContactsList />
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Historique des envois</CardTitle>
                <CardDescription>
                  Consultez l'historique de tous vos envois de newsletters
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Newsletter</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Destinataires</TableHead>
                      <TableHead>Résultat</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {newsletters
                      .filter(n => n.status === "sent")
                      .map((newsletter) => (
                        <TableRow key={newsletter.id}>
                          <TableCell className="font-medium">{newsletter.title_internal}</TableCell>
                          <TableCell>
                            <Badge variant="outline">Manuel</Badge>
                          </TableCell>
                          <TableCell>-</TableCell>
                          <TableCell>
                            <Badge className="bg-green-500">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Succès
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {newsletter.sent_at 
                              ? format(parseISO(newsletter.sent_at), "d MMM yyyy HH:mm", { locale: fr })
                              : "-"
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedNewsletter ? "Modifier la newsletter" : "Créer une newsletter"}
            </DialogTitle>
            <DialogDescription>
              Remplissez les informations de votre newsletter
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title_internal">Titre interne *</Label>
                <Input
                  id="title_internal"
                  value={formData.title_internal}
                  onChange={(e) => setFormData({ ...formData, title_internal: e.target.value })}
                  placeholder="Ex: Newsletter Mars 2026"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">Objet de l'email *</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="L'objet qui apparaîtra dans la boîte mail"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="preheader">Pré-header (optionnel)</Label>
              <Input
                id="preheader"
                value={formData.preheader}
                onChange={(e) => setFormData({ ...formData, preheader: e.target.value })}
                placeholder="Texte d'aperçu visible avant l'ouverture"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cover_image_url">Image de couverture (URL)</Label>
              <div className="flex gap-2">
                <Input
                  id="cover_image_url"
                  value={formData.cover_image_url}
                  onChange={(e) => setFormData({ ...formData, cover_image_url: e.target.value })}
                  placeholder="https://..."
                />
                <Button variant="outline" size="icon">
                  <ImageIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="content_html">Contenu HTML *</Label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="gap-1 text-primary"
                  onClick={() => setShowHelpDialog(true)}
                >
                  <BookOpen className="w-4 h-4" />
                  Aide éditoriale
                </Button>
              </div>
              <Textarea
                id="content_html"
                value={formData.content_html}
                onChange={(e) => setFormData({ ...formData, content_html: e.target.value })}
                placeholder="<h2>Titre de section</h2><p>Votre contenu...</p>"
                className="min-h-[200px] font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Utilisez du HTML simple : &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;a&gt;, &lt;strong&gt;, &lt;em&gt;
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cta_label">Bouton CTA - Texte</Label>
                <Input
                  id="cta_label"
                  value={formData.cta_label}
                  onChange={(e) => setFormData({ ...formData, cta_label: e.target.value })}
                  placeholder="Ex: Prendre rendez-vous"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cta_url">Bouton CTA - URL</Label>
                <Input
                  id="cta_url"
                  value={formData.cta_url}
                  onChange={(e) => setFormData({ ...formData, cta_url: e.target.value })}
                  placeholder="https://regard-de-nature.fr/..."
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Annuler
            </Button>
            <Button variant="secondary" onClick={() => handleSave(true)}>
              <FileText className="w-4 h-4 mr-2" />
              Enregistrer brouillon
            </Button>
            <Button onClick={() => {
              handleSave(true);
              // After save, open scheduler
            }}>
              <CalendarIcon className="w-4 h-4 mr-2" />
              Enregistrer et planifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scheduler Dialog */}
      <Dialog open={showScheduler} onOpenChange={setShowScheduler}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Planifier l'envoi</DialogTitle>
            <DialogDescription>
              Choisissez la date et l'heure d'envoi de votre newsletter
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date d'envoi</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !scheduleDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {scheduleDate ? format(scheduleDate, "PPP", { locale: fr }) : "Sélectionner une date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={scheduleDate}
                    onSelect={setScheduleDate}
                    initialFocus
                    disabled={(date) => date < new Date()}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="schedule-time">Heure d'envoi (Europe/Paris)</Label>
              <Select value={scheduleTime} onValueChange={setScheduleTime}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => {
                    const hour = i.toString().padStart(2, "0");
                    return (
                      <SelectItem key={hour} value={`${hour}:00`}>
                        {hour}:00
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduler(false)}>
              Annuler
            </Button>
            <Button onClick={handleSchedule} disabled={scheduling || !scheduleDate}>
              {scheduling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
              Programmer l'envoi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Prévisualisation</DialogTitle>
            <DialogDescription>
              Aperçu de votre newsletter telle qu'elle sera envoyée
            </DialogDescription>
          </DialogHeader>
          
          {selectedNewsletter && (
            <div className="border rounded-lg overflow-hidden bg-white">
              <iframe
                srcDoc={generatePreviewHtml(selectedNewsletter)}
                className="w-full h-[500px]"
                title="Preview"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Logs Dialog */}
      <Dialog open={showLogs} onOpenChange={setShowLogs}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Logs d'envoi</DialogTitle>
            <DialogDescription>
              Historique des tentatives d'envoi pour cette newsletter
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {sendLogs.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucun envoi effectué pour cette newsletter
              </p>
            ) : (
              <div className="space-y-3">
                {sendLogs.map((log) => (
                  <Card key={log.id} className={cn(
                    "border-l-4",
                    log.success ? "border-l-green-500" : "border-l-red-500"
                  )}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            {log.success ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-red-500" />
                            )}
                            <span className="font-medium">
                              {log.success ? "Envoi réussi" : "Échec"}
                            </span>
                            <Badge variant="outline">{log.run_type}</Badge>
                          </div>
                          {log.recipients_count && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {log.recipients_count} destinataire(s)
                            </p>
                          )}
                          {log.error_message && (
                            <p className="text-sm text-red-600 mt-1">
                              {log.error_message}
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(parseISO(log.created_at), "d MMM yyyy HH:mm", { locale: fr })}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Editorial Help Dialog */}
      <Dialog open={showHelpDialog} onOpenChange={setShowHelpDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Guide de création de newsletter
            </DialogTitle>
            <DialogDescription>
              Tout ce que vous devez savoir pour créer une newsletter efficace et engageante
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[60vh] pr-4">
            <div className="space-y-6">
              {/* Introduction */}
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-primary mb-1">Pourquoi une newsletter ?</h4>
                      <p className="text-sm text-muted-foreground">
                        Votre newsletter est un rendez-vous privilégié avec vos abonnés. Elle vous permet de 
                        partager vos connaissances, créer du lien et accompagner vos lecteurs dans leur 
                        quête de bien-être naturel. Une newsletter régulière renforce votre expertise et 
                        fidélise votre communauté.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Accordion type="single" collapsible className="w-full" defaultValue="structure">
                {/* Structure recommandée */}
                <AccordionItem value="structure">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-primary" />
                      <span>Structure recommandée</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pl-6">
                      <p className="text-sm text-muted-foreground">
                        Une structure en 3 parties fonctionne très bien :
                      </p>
                      <div className="space-y-3">
                        <div className="border-l-2 border-primary/30 pl-4">
                          <h5 className="font-medium text-sm">1. Comprendre 💡</h5>
                          <p className="text-sm text-muted-foreground">
                            Expliquez un concept, une plante, une problématique de saison. 
                            Vulgarisez sans simplifier à l'excès.
                          </p>
                        </div>
                        <div className="border-l-2 border-primary/30 pl-4">
                          <h5 className="font-medium text-sm">2. Pratiquer 🌿</h5>
                          <p className="text-sm text-muted-foreground">
                            Proposez des conseils concrets, recettes, rituels ou exercices 
                            que vos lecteurs peuvent appliquer immédiatement.
                          </p>
                        </div>
                        <div className="border-l-2 border-primary/30 pl-4">
                          <h5 className="font-medium text-sm">3. Aller plus loin 🎯</h5>
                          <p className="text-sm text-muted-foreground">
                            Invitez à l'action : prendre rendez-vous, participer à un atelier, 
                            découvrir un produit, ou simplement répondre à la newsletter.
                          </p>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Idées de thèmes */}
                <AccordionItem value="themes">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      <span>Idées de thèmes par saison</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pl-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card className="bg-green-50/50 border-green-200">
                          <CardContent className="pt-4">
                            <h5 className="font-medium text-sm text-green-800 mb-2">🌱 Printemps</h5>
                            <ul className="text-xs text-green-700 space-y-1">
                              <li>• Détox douce et drainage</li>
                              <li>• Les allergies saisonnières</li>
                              <li>• Renouveau et énergie</li>
                              <li>• Plantes dépuratives</li>
                            </ul>
                          </CardContent>
                        </Card>
                        <Card className="bg-yellow-50/50 border-yellow-200">
                          <CardContent className="pt-4">
                            <h5 className="font-medium text-sm text-yellow-800 mb-2">☀️ Été</h5>
                            <ul className="text-xs text-yellow-700 space-y-1">
                              <li>• Protection solaire naturelle</li>
                              <li>• Hydratation et vitalité</li>
                              <li>• Jambes légères</li>
                              <li>• Voyage et bien-être</li>
                            </ul>
                          </CardContent>
                        </Card>
                        <Card className="bg-orange-50/50 border-orange-200">
                          <CardContent className="pt-4">
                            <h5 className="font-medium text-sm text-orange-800 mb-2">🍂 Automne</h5>
                            <ul className="text-xs text-orange-700 space-y-1">
                              <li>• Renforcer l'immunité</li>
                              <li>• Préparation à l'hiver</li>
                              <li>• Gérer le blues automnal</li>
                              <li>• Cure de vitamines naturelles</li>
                            </ul>
                          </CardContent>
                        </Card>
                        <Card className="bg-blue-50/50 border-blue-200">
                          <CardContent className="pt-4">
                            <h5 className="font-medium text-sm text-blue-800 mb-2">❄️ Hiver</h5>
                            <ul className="text-xs text-blue-700 space-y-1">
                              <li>• Booster ses défenses</li>
                              <li>• Sommeil et récupération</li>
                              <li>• Confort digestif des fêtes</li>
                              <li>• Gérer le stress de fin d'année</li>
                            </ul>
                          </CardContent>
                        </Card>
                      </div>
                      <p className="text-xs text-muted-foreground italic">
                        💡 Astuce : Planifiez vos thèmes 2-3 mois à l'avance pour rester cohérent avec les saisons.
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Rédaction */}
                <AccordionItem value="writing">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <PenTool className="w-4 h-4 text-primary" />
                      <span>Conseils de rédaction</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pl-6">
                      <div className="space-y-3">
                        <div className="flex items-start gap-2">
                          <Heart className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <h5 className="font-medium text-sm">Ton chaleureux et accessible</h5>
                            <p className="text-xs text-muted-foreground">
                              Écrivez comme si vous parliez à un(e) ami(e). Utilisez le "vous" 
                              et créez de la proximité. Évitez le jargon technique.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Leaf className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <h5 className="font-medium text-sm">Longueur idéale</h5>
                            <p className="text-xs text-muted-foreground">
                              Entre 300 et 600 mots. Assez pour apporter de la valeur, 
                              pas trop pour garder l'attention. Privilégiez les paragraphes courts.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Target className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <h5 className="font-medium text-sm">Un seul message principal</h5>
                            <p className="text-xs text-muted-foreground">
                              Chaque newsletter = un thème principal. Mieux vaut approfondir 
                              un sujet que survoler plusieurs.
                            </p>
                          </div>
                        </div>
                      </div>
                      <Card className="bg-muted/50">
                        <CardContent className="pt-4">
                          <h5 className="font-medium text-sm mb-2">Formules d'accroche efficaces :</h5>
                          <ul className="text-xs text-muted-foreground space-y-1">
                            <li>• "Saviez-vous que..." (curiosité)</li>
                            <li>• "Si vous êtes concerné(e) par..." (identification)</li>
                            <li>• "Ce mois-ci, j'ai envie de vous parler de..." (proximité)</li>
                            <li>• "Une question que l'on me pose souvent..." (expertise)</li>
                          </ul>
                        </CardContent>
                      </Card>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Objet email */}
                <AccordionItem value="subject">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-primary" />
                      <span>Rédiger un bon objet d'email</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pl-6">
                      <p className="text-sm text-muted-foreground">
                        L'objet est crucial : c'est lui qui détermine si votre email sera ouvert !
                      </p>
                      <div className="space-y-2">
                        <h5 className="font-medium text-sm">Bonnes pratiques :</h5>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li>✅ Court : 40-50 caractères maximum</li>
                          <li>✅ Susciter la curiosité sans clickbait</li>
                          <li>✅ Personnaliser si possible (prénom)</li>
                          <li>✅ Éviter les mots spam (GRATUIT, URGENT, !!!)</li>
                        </ul>
                      </div>
                      <Card className="bg-muted/50">
                        <CardContent className="pt-4">
                          <h5 className="font-medium text-sm mb-2">Exemples d'objets efficaces :</h5>
                          <ul className="text-xs text-muted-foreground space-y-1">
                            <li>• "🌿 3 plantes pour mieux dormir"</li>
                            <li>• "Fatigue de printemps ? Mes conseils"</li>
                            <li>• "Ce que j'aurais aimé savoir sur le stress"</li>
                            <li>• "Votre rituel bien-être de janvier"</li>
                          </ul>
                        </CardContent>
                      </Card>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* HTML */}
                <AccordionItem value="html">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" />
                      <span>Balises HTML à utiliser</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pl-6">
                      <p className="text-sm text-muted-foreground">
                        Le contenu est en HTML. Voici les balises les plus utiles :
                      </p>
                      <div className="bg-muted rounded-lg p-4 font-mono text-xs space-y-2">
                        <div><span className="text-primary">&lt;h2&gt;</span>Titre de section<span className="text-primary">&lt;/h2&gt;</span></div>
                        <div><span className="text-primary">&lt;h3&gt;</span>Sous-titre<span className="text-primary">&lt;/h3&gt;</span></div>
                        <div><span className="text-primary">&lt;p&gt;</span>Un paragraphe de texte<span className="text-primary">&lt;/p&gt;</span></div>
                        <div><span className="text-primary">&lt;strong&gt;</span>Texte en gras<span className="text-primary">&lt;/strong&gt;</span></div>
                        <div><span className="text-primary">&lt;em&gt;</span>Texte en italique<span className="text-primary">&lt;/em&gt;</span></div>
                        <div><span className="text-primary">&lt;ul&gt;&lt;li&gt;</span>Élément de liste<span className="text-primary">&lt;/li&gt;&lt;/ul&gt;</span></div>
                        <div><span className="text-primary">&lt;a href="URL"&gt;</span>Texte du lien<span className="text-primary">&lt;/a&gt;</span></div>
                        <div><span className="text-primary">&lt;br&gt;</span> — Retour à la ligne</div>
                      </div>
                      <Card className="bg-muted/50">
                        <CardContent className="pt-4">
                          <h5 className="font-medium text-sm mb-2">Exemple complet :</h5>
                          <pre className="text-xs text-muted-foreground whitespace-pre-wrap bg-background p-3 rounded border">
{`<h2>🌿 Comprendre le sommeil</h2>
<p>Le sommeil est <strong>essentiel</strong> pour notre vitalité...</p>

<h3>Mes 3 conseils pratiques</h3>
<ul>
  <li>Évitez les écrans 1h avant le coucher</li>
  <li>Créez un rituel apaisant</li>
  <li>Optez pour une tisane de tilleul</li>
</ul>

<p><em>Prenez soin de vous,</em><br>
Votre naturopathe</p>`}
                          </pre>
                        </CardContent>
                      </Card>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Précautions légales */}
                <AccordionItem value="legal">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <span>Précautions légales importantes</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pl-6">
                      <Card className="border-amber-200 bg-amber-50/50">
                        <CardContent className="pt-4">
                          <h5 className="font-medium text-sm text-amber-800 mb-2">⚠️ À éviter absolument :</h5>
                          <ul className="text-xs text-amber-700 space-y-1">
                            <li>❌ Claims médicaux ("soigne", "guérit", "traite")</li>
                            <li>❌ Remplacer un avis médical</li>
                            <li>❌ Promettre des résultats garantis</li>
                            <li>❌ Diagnostics ou prescriptions</li>
                          </ul>
                        </CardContent>
                      </Card>
                      <Card className="border-green-200 bg-green-50/50">
                        <CardContent className="pt-4">
                          <h5 className="font-medium text-sm text-green-800 mb-2">✅ Formulations recommandées :</h5>
                          <ul className="text-xs text-green-700 space-y-1">
                            <li>• "Traditionnellement utilisé pour..."</li>
                            <li>• "Peut contribuer à..."</li>
                            <li>• "Est réputé pour ses propriétés..."</li>
                            <li>• "En complément d'un suivi médical..."</li>
                            <li>• "Demandez conseil à votre médecin si..."</li>
                          </ul>
                        </CardContent>
                      </Card>
                      <p className="text-xs text-muted-foreground italic">
                        💡 En cas de doute, ajoutez toujours une mention invitant à consulter un professionnel de santé.
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Checklist finale */}
              <Card className="border-primary/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    Checklist avant envoi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>☐ Objet accrocheur et court (40-50 car.)</li>
                    <li>☐ Pré-header complète l'objet</li>
                    <li>☐ Structure claire (Comprendre / Pratiquer / Aller plus loin)</li>
                    <li>☐ Ton chaleureux et professionnel</li>
                    <li>☐ Pas de claims médicaux</li>
                    <li>☐ CTA clair si pertinent</li>
                    <li>☐ Relecture orthographique</li>
                    <li>☐ Test de prévisualisation</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHelpDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsletterManagement;
